import { IconArrowRight } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useLoaderData } from "react-router";

// Marketing labels, applied by tier index. The API only ships ranges + rates.
const TIER_NAMES = ["Starter", "Growth", "Scale", "Volume", "Enterprise"];
const TIER_EYEBROWS = [
  "Start small",
  "Most teams",
  "High volume",
  "Best rate",
  "Enterprise",
];

const SLIDER_MAX = 150000;

const numberFmt = new Intl.NumberFormat("en-US");
const currencyFmt = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});

// "$0.05" / "$0.019" — 3 decimal precision, trailing zeros trimmed past the
// second decimal so whole-cent rates look natural.
function formatRate(rate) {
  const str = rate.toFixed(3);
  const trimmed = str.replace(/0+$/, "").replace(/\.$/, "");
  // Make sure we never end up with fewer than 2 decimals.
  const [whole, frac = ""] = trimmed.split(".");
  return `$${whole}.${frac.padEnd(2, "0")}`;
}

function buildTiers(apiTiers) {
  return apiTiers.map((t, i) => ({
    ...t,
    name: TIER_NAMES[i] ?? `Tier ${i + 1}`,
    eyebrow: TIER_EYEBROWS[i] ?? "Volume",
    range: t.label,
    rateLabel: formatRate(t.rate),
  }));
}

function getTierForCredits(credits, tiers) {
  return [...tiers].reverse().find((tier) => credits >= tier.min) || tiers[0];
}

function RatePill({ tier, variant = "default" }) {
  const styles =
    variant === "dark"
      ? "bg-white/10 border border-white/10 text-white"
      : "bg-bg border border-white/8 text-text";
  return (
    <div
      className={`inline-flex items-baseline gap-2 rounded-full px-4 py-2 text-sm font-semibold ${styles}`}
    >
      <span>{tier.rateLabel}</span>
      <span className="text-xs font-medium opacity-50">/ credit</span>
    </div>
  );
}

export function Pricing() {
  const { creditTiers } = useLoaderData();
  const tiers = useMemo(() => buildTiers(creditTiers), [creditTiers]);
  const presets = useMemo(() => tiers.map((t) => t.min), [tiers]);
  const minCredits = tiers[0]?.min ?? 1000;

  const [credits, setCredits] = useState(minCredits);

  const selectedTier = useMemo(
    () => getTierForCredits(credits, tiers),
    [credits, tiers]
  );
  const total = credits * selectedTier.rate;
  const sliderPct = ((credits - minCredits) / (SLIDER_MAX - minCredits)) * 100;

  return (
    <section
      className="relative overflow-hidden px-6 py-32 sm:px-8 sm:py-40"
    >
      {/* Soft glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute left-1/2 top-1/2 h-125 w-200 -translate-x-1/2 -translate-y-1/2 rounded-full bg-main-500/8 blur-[160px]"
      />

      <div id="pricing" className="relative mx-auto max-w-360 scroll-mt-24">
        {/* Header */}
        <div className="mb-14 flex flex-col justify-between gap-8 lg:flex-row lg:items-end">
          <div className="max-w-2xl">
            <p className="mb-4 text-[10px] font-semibold uppercase tracking-[0.28em] text-main-300">
              Pricing
            </p>
            <h2 className="text-4xl font-bold tracking-[-0.03em] text-text sm:text-5xl md:text-6xl">
              See exactly what
              <br />
              your credits cost.
            </h2>
          </div>
          <p className="max-w-sm text-base leading-7 text-text-soft lg:text-right">
            Move the slider or pick a preset to see the active tier, the rate
            per credit, and your estimated total in real time.
          </p>
        </div>

        {/* Bento grid */}
        <div className="grid gap-4 lg:grid-cols-12">

          {/* ── Calculator — col-span-7 ── */}
          <article className="col-span-12 flex flex-col overflow-hidden rounded-3xl bg-card-2 p-8 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.08),inset_0_1px_0_0_rgba(255,255,255,0.07)] lg:col-span-7">
            <div className="mb-10 flex flex-wrap items-start justify-between gap-4">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-main-300">
                  Estimate cost
                </p>
                <h3 className="mt-3 text-5xl font-bold tracking-[-0.04em] text-text">
                  {numberFmt.format(credits)} <span className="text-text-muted">credits</span>
                </h3>
              </div>
              <RatePill tier={selectedTier} />
            </div>

            <div className="relative">
              <input
                type="range"
                min={minCredits}
                max={SLIDER_MAX}
                step={1000}
                value={credits}
                onChange={(e) => setCredits(Number(e.target.value))}
                style={{
                  background: `linear-gradient(to right, var(--color-main-500) 0%, var(--color-main-500) ${sliderPct}%, rgba(255,255,255,0.06) ${sliderPct}%, rgba(255,255,255,0.06) 100%)`,
                }}
                className="
                  h-1.5 w-full cursor-pointer appearance-none rounded-full
                  shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)]
                  [&::-webkit-slider-thumb]:h-6
                  [&::-webkit-slider-thumb]:w-1.5
                  [&::-webkit-slider-thumb]:appearance-none
                  [&::-webkit-slider-thumb]:rounded-full
                  [&::-webkit-slider-thumb]:bg-white
                  [&::-webkit-slider-thumb]:cursor-grab
                  [&::-webkit-slider-thumb]:transition-[height,box-shadow]
                  [&::-webkit-slider-thumb]:shadow-[0_0_12px_rgba(255,255,255,0.45)]
                  [&::-webkit-slider-thumb]:hover:h-7
                  [&::-webkit-slider-thumb]:active:h-7
                  [&::-webkit-slider-thumb]:active:cursor-grabbing
                  [&::-moz-range-thumb]:h-6
                  [&::-moz-range-thumb]:w-1.5
                  [&::-moz-range-thumb]:rounded-full
                  [&::-moz-range-thumb]:border-0
                  [&::-moz-range-thumb]:bg-white
                  [&::-moz-range-thumb]:cursor-grab
                  [&::-moz-range-thumb]:transition-[height,box-shadow]
                  [&::-moz-range-thumb]:shadow-[0_0_12px_rgba(255,255,255,0.45)]
                  [&::-moz-range-thumb]:hover:h-7
                  [&::-moz-range-thumb]:active:h-7
                "
                aria-label="Credit amount"
              />
              <div className="mt-3 flex justify-between font-mono text-[10px] text-text-muted">
                {[minCredits, Math.round(SLIDER_MAX / 3), Math.round((SLIDER_MAX * 2) / 3), SLIDER_MAX].map(
                  (v) => (
                    <span key={v}>{v >= 1000 ? `${Math.round(v / 1000)}K` : v}</span>
                  )
                )}
              </div>
            </div>

            <div className="mt-auto pt-8 grid gap-3 sm:grid-cols-4">
              {presets.map((amount) => {
                const active = credits === amount;
                return (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => setCredits(amount)}
                    className={`rounded-2xl p-4 text-left text-sm font-medium transition ${
                      active
                        ? "bg-main-500/15 text-text shadow-[inset_0_0_0_1px_rgba(63,96,214,0.55)]"
                        : "bg-bg text-text-soft shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] hover:text-text hover:shadow-[inset_0_0_0_1px_rgba(63,96,214,0.35)]"
                    }`}
                  >
                    {numberFmt.format(amount)}
                    <span className="ml-1 text-text-muted">credits</span>
                  </button>
                );
              })}
            </div>
          </article>

          {/* ── Active tier — col-span-5 ── */}
          <article className="relative col-span-12 flex flex-col overflow-hidden rounded-3xl bg-bg px-8 pt-5 pb-8 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.06)] lg:col-span-5">
            <div
              aria-hidden
              className="pointer-events-none absolute -right-8 -top-8 h-48 w-48 rounded-full bg-main-500/20 blur-3xl"
            />

            <p className="relative text-sm font-medium text-text-muted">
              Active tier
            </p>
            <h3 className="relative mt-3 mb-8 text-5xl font-bold tracking-[-0.04em] text-text">
              {selectedTier.name}
            </h3>

            <div className="relative mt-auto rounded-2xl border border-white/8 bg-white/4 p-5">
              <div className="flex items-center justify-between gap-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-text-muted">
                  Estimated total
                </p>
                <span className="rounded-full bg-white/8 px-2.5 py-1 font-mono text-[10px] text-text-soft">
                  {selectedTier.range}
                </span>
              </div>
              <p className="mt-3 text-5xl font-bold tracking-[-0.04em] text-text">
                {currencyFmt.format(total)}
              </p>

              <a
                href="https://app.titanskip.com"
                className="group mt-5 inline-flex h-11 w-full items-center justify-center gap-2 rounded-full bg-main-500 px-5 text-sm font-semibold text-white transition hover:bg-main-400"
              >
                Go to checkout
                <span
                  aria-hidden
                  className="grid h-6 w-6 place-items-center rounded-full bg-white/15 transition group-hover:translate-x-0.5 group-hover:bg-white/25"
                >
                  <IconArrowRight size={12} stroke={2.6} />
                </span>
              </a>
            </div>
          </article>

          {/* ── All tiers — col-span-12 ── */}
          <div className="col-span-12 grid gap-3 grid-cols-2 lg:grid-cols-4">
            {tiers.map((tier) => {
              const isActive = selectedTier.name === tier.name;
              return (
                <button
                  key={tier.name}
                  type="button"
                  onClick={() => setCredits(tier.min)}
                  className={`rounded-2xl p-5 text-left transition ${
                    isActive
                      ? "bg-main-500 text-white shadow-[inset_0_1px_0_0_rgba(255,255,255,0.18)]"
                      : "bg-card text-text-soft shadow-[inset_0_0_0_1px_rgba(255,255,255,0.07)] hover:bg-card-2 hover:shadow-[inset_0_0_0_1px_rgba(63,96,214,0.35)]"
                  }`}
                >
                  <p
                    className={`text-[10px] font-semibold uppercase tracking-[0.2em] ${
                      isActive ? "text-white/70" : "text-main-300/80"
                    }`}
                  >
                    {tier.eyebrow}
                  </p>
                  <p
                    className={`mt-2 text-lg font-bold tracking-[-0.02em] ${
                      isActive ? "text-white" : "text-text"
                    }`}
                  >
                    {tier.name}
                  </p>
                  <p className={`mt-1 text-xs ${isActive ? "text-white/70" : "text-text-muted"}`}>
                    {tier.range}
                  </p>
                  <p
                    className={`mt-4 text-2xl font-bold tracking-[-0.03em] ${
                      isActive ? "text-white" : "text-text"
                    }`}
                  >
                    {tier.rateLabel}
                  </p>
                  <p className={`mt-1 text-xs ${isActive ? "text-white/70" : "text-text-muted"}`}>
                    per credit
                  </p>
                </button>
              );
            })}
          </div>

        </div>
      </div>
    </section>
  );
}
